import React, { useEffect, useState, useRef, useCallback } from "react";
import { db } from "./firebase";
import { ref, onValue, remove } from "firebase/database";
import { useNavigate } from "react-router-dom";
import { Outlet } from "react-router-dom";

// --- Styles ---
const inputStyle = { 
  padding: "8px 14px", 
  border: "1px solid #ccc", 
  borderRadius: 6, 
  fontSize: 14, 
  width: "100%",
  boxSizing: "border-box"
};

const btnStyle = { 
  padding: "8px 14px", 
  background: "#007bff", 
  color: "#fff", 
  border: "none", 
  borderRadius: 6, 
  cursor: "pointer", 
  marginRight: 10 
};

const th = { 
  textAlign: "left", 
  padding: 12, 
  background: "#f3f4f6", 
  fontWeight: 600, 
  fontSize: 14, 
  color: "#111827" 
};

const td = { 
  padding: 12, 
  borderBottom: "1px solid #e5e7eb", 
  fontSize: 14, 
  color: "#374151" 
};

const tableStyle = { 
  width: "100%", 
  borderCollapse: "collapse", 
  borderRadius: 12, 
  overflow: "hidden", 
  boxShadow: "0 10px 30px rgba(0,0,0,.05)", 
  background: "#fff", 
  fontFamily: "'Inter', sans-serif" 
};

const imageStyle = { 
  width: 40, 
  height: 40, 
  borderRadius: 4, 
  objectFit: "cover" 
};

const shimmerRowStyle = { 
  height: 50, 
  marginBottom: 10, 
  borderRadius: 6, 
  background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)", 
  backgroundSize: "200% 100%", 
  animation: "shimmer 1.5s infinite" 
};

const shimmerKeyframes = `
@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
`;

// --- Component ---
export default function ProductsPage({ uid }) {
  const [allProducts, setAllProducts] = useState([]); // كل المنتجات من Firebase
  const [displayedProducts, setDisplayedProducts] = useState([]); // المنتجات المعروضة بعد التصفية
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterBrand, setFilterBrand] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [lastKey, setLastKey] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const navigate = useNavigate();
  const containerRef = useRef();
  const searchTimeoutRef = useRef();
  const [salesMap, setSalesMap] = useState({});
  const [salesFilter, setSalesFilter] = useState(""); 

  const PAGE_SIZE = 10;

  // Load brands & categories
  useEffect(() => {
    if (!uid) return;

    const brandsRef = ref(db, `distributors/${uid}/brands`);
    const unsubBrands = onValue(brandsRef, snap => {
      const val = snap.val();
      setBrands(val ? Object.keys(val).map(id => ({ id, ...val[id] })) : []);
    });
    
    const categoriesRef = ref(db, `distributors/${uid}/categories`);
    const unsubCats = onValue(categoriesRef, snap => {
      const val = snap.val();
      setCategories(val ? Object.keys(val).map(id => ({ id, ...val[id] })) : []);
    });
    
    return () => {
      unsubBrands();
      unsubCats();
    };
  }, [uid]);

  // Load all products once
  useEffect(() => {
    if (!uid) return;

    setLoading(true);
    const productsRef = ref(db, `distributors/${uid}/products`);
    
    const unsub = onValue(productsRef, (snap) => {
      const val = snap.val() || {};
      const productsArray = Object.keys(val).map(key => ({ 
        id: key, 
        ...val[key],
        // Ensure all searchable fields have string values
        name: val[key].name || "",
        barcode: val[key].barcode || "",
        description: val[key].description || "",
        brandName: val[key].brandName || "",
        categoryName: val[key].categoryName || "",
        price: val[key].price || 0,
        purchasePrice: val[key].purchasePrice || 0,
        quantity: val[key].quantity || 0
      }));

      // Sort by createdAt (newest first)
      productsArray.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      
      setAllProducts(productsArray);
      // Initial display - first page
      setDisplayedProducts(productsArray.slice(0, PAGE_SIZE));
      setLastKey(PAGE_SIZE);
      setHasMore(productsArray.length > PAGE_SIZE);
      setLoading(false);
    });

    return () => unsub();
  }, [uid]);

  // Apply filters and search
const applyFilters = useCallback(() => {
  let filtered = [...allProducts];

  // Apply brand filter
  if (filterBrand) filtered = filtered.filter(p => p.brandName === filterBrand);

  // Apply category filter
  if (filterCategory) filtered = filtered.filter(p => p.categoryName === filterCategory);

  // Apply search query
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase().trim();
    filtered = filtered.filter(product => {
      return (
        (product.name?.toLowerCase().includes(query)) ||
        (product.barcode?.toLowerCase().includes(query)) ||
        (product.description?.toLowerCase().includes(query)) ||
        (product.brandName?.toLowerCase().includes(query)) ||
        (product.categoryName?.toLowerCase().includes(query)) ||
        (product.price?.toString().includes(query)) ||
        (product.purchasePrice?.toString().includes(query)) ||
        (product.quantity?.toString().includes(query))
      );
    });
  }

  // Sales filter
  if (salesFilter === "sold") {
    filtered = filtered.filter(p => (salesMap[p.id] || 0) > 0);
  } else if (salesFilter === "unsold") {
    filtered = filtered.filter(p => (salesMap[p.id] || 0) === 0);
  }

  // Sort by sales
  filtered.sort((a, b) => (salesMap[b.id] || 0) - (salesMap[a.id] || 0));

  // Reset pagination
  setDisplayedProducts(filtered.slice(0, PAGE_SIZE));
  setLastKey(PAGE_SIZE);
  setHasMore(filtered.length > PAGE_SIZE);

}, [allProducts, filterBrand, filterCategory, searchQuery, salesMap, salesFilter]);
// calcul nombre produit vendu
  useEffect(() => {
  if (!uid) return;

  const ordersRef = ref(db, `distributors/${uid}/orders`);

  const unsub = onValue(ordersRef, (snap) => {
    const val = snap.val() || {};
    const sales = {};

    Object.values(val).forEach(order => {
       if (order.status !== "Terminé") return;
      if (order.products) {
        Object.values(order.products).forEach(prod => {
          const key = prod.productKey;

          if (!key) return;

          if (!sales[key]) {
            sales[key] = 0;
          }

          sales[key] += prod.quantity || 0;
        });
      }
    });

    setSalesMap(sales);
  });

  return () => unsub();
}, [uid]);

  // Apply filters when dependencies change
  useEffect(() => {
    if (allProducts.length > 0) {
      applyFilters();
    }
  }, [allProducts, filterBrand, filterCategory, searchQuery, salesMap,, salesFilter]);

  // Load more products on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current || loading || !hasMore || searchQuery.trim()) return;
      
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      const scrollPosition = scrollTop + clientHeight;
      
      if (scrollPosition >= scrollHeight - 100) {
        loadMoreProducts();
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [loading, hasMore, searchQuery]);

  // Load more products function
 const loadMoreProducts = () => {
  if (loading || !hasMore) return;

  setLoading(true);

  setTimeout(() => {
    let filtered = [...allProducts];

    if (filterBrand) filtered = filtered.filter(p => p.brandName === filterBrand);
    if (filterCategory) filtered = filtered.filter(p => p.categoryName === filterCategory);

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(product => {
        return (
          (product.name?.toLowerCase().includes(query)) ||
          (product.barcode?.toLowerCase().includes(query)) ||
          (product.description?.toLowerCase().includes(query)) ||
          (product.brandName?.toLowerCase().includes(query)) ||
          (product.categoryName?.toLowerCase().includes(query)) ||
          (product.price?.toString().includes(query)) ||
          (product.purchasePrice?.toString().includes(query)) ||
          (product.quantity?.toString().includes(query))
        );
      });
    }

    if (salesFilter === "sold") {
      filtered = filtered.filter(p => (salesMap[p.id] || 0) > 0);
    } else if (salesFilter === "unsold") {
      filtered = filtered.filter(p => (salesMap[p.id] || 0) === 0);
    }

    // Sort by sales
    filtered.sort((a, b) => (salesMap[b.id] || 0) - (salesMap[a.id] || 0));

    const nextProducts = filtered.slice(lastKey, lastKey + PAGE_SIZE);

    setDisplayedProducts(prev => [...prev, ...nextProducts]);
    setLastKey(prev => prev + PAGE_SIZE);
    setHasMore(filtered.length > lastKey + PAGE_SIZE);
    setLoading(false);
  }, 500);
};

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      applyFilters();
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, applyFilters]);

  // Delete product
  const handleDelete = async (id) => {
    if (!window.confirm("Voulez-vous vraiment supprimer ce produit ?")) return;
    
    try {
      await remove(ref(db, `distributors/${uid}/products/${id}`));
      // المنتج سيتم حذفه تلقائياً من allProducts بسبب الـ onValue listener
    } catch (error) {
      console.error("Error deleting product:", error);
      alert("Erreur lors de la suppression du produit");
    }
  };

  // Reset all filters
  const handleResetFilters = () => {
    setFilterBrand("");
    setFilterCategory("");
    setSearchQuery("");
  };

  return (
    <div style={{ padding: 20, height: "80vh", overflowY: "auto" }} ref={containerRef}>
      <style>{shimmerKeyframes}</style>
      
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <h2>Produits</h2>

        <div style={{ display: "flex", gap: 10 }}>
          <button
           style={{
           ...btnStyle,
          background: "#dc3545",
          }}
          onClick={() => navigate("/products/low-stock")}
          >
          ⚠️ Stock faible
         </button>

          
          <button 
            style={{
              ...btnStyle,
              background: "#d65e0eff",
            }}
            onClick={() => navigate("categories")}
          >
            Catégories
          </button>

          <button
            style={{
              ...btnStyle,
              background: "#10b981",
            }}
            onClick={() => navigate("brands")}
          >
            Marques
          </button>
          
          <button
            style={btnStyle}
            onClick={() => navigate("/products/add")}
          >
            + Ajouter un produit
          </button>
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
  {[
    { label: "Produits vendus", value: "sold", activeColor: "#10b981" },
    { label: "Produits non vendus", value: "unsold", activeColor: "#dc3545" },
    { label: "Tous les produits", value: "", activeColor: "#6c757d" },
  ].map(btn => (
    <button
      key={btn.value}
      style={{
        ...btnStyle,
        background: salesFilter === btn.value ? btn.activeColor : "#007bff",
        fontSize: 13,
        padding: "6px 12px"
      }}
      onClick={() => setSalesFilter(btn.value)}
    >
      {btn.label}
    </button>
  ))}
</div>

      </div>

      {/* Filters and Search */}
      <div style={{ 
        display: "grid", 
        gridTemplateColumns: "1fr 1fr 1fr", 
        gap: 12, 
        marginBottom: 20,
        alignItems: "end"
      }}>
        {/* Search Bar - Now in its own column */}
        <div style={{ position: "relative" }}>
          <div style={{ 
            display: "flex", 
            alignItems: "center",
            gap: 8
          }}>
            <div style={{ position: "relative", flex: 1 }}>
              <input
                type="text"
                placeholder="Rechercher un produit..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  ...inputStyle,
                  paddingLeft: 40,
                }}
              />
              <span style={{
                position: "absolute",
                left: 12,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#666",
                fontSize: 16
              }}>
                🔍
              </span>
              
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    color: "#999",
                    cursor: "pointer",
                    fontSize: 20,
                    width: 24,
                    height: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Brand Filter */}
        <div>
          <label style={{ 
            display: "block", 
            marginBottom: 6, 
            fontSize: 14, 
            color: "#555",
            fontWeight: 500
          }}>
            Marque
          </label>
          <select 
            value={filterBrand} 
            onChange={e => setFilterBrand(e.target.value)} 
            style={inputStyle}
          >
            <option value="">Toutes les marques</option>
            {brands.map(b => (
              <option key={b.id} value={b.name}>{b.name}</option>
            ))}
          </select>
        </div>

        {/* Category Filter */}
        <div>
          <label style={{ 
            display: "block", 
            marginBottom: 6, 
            fontSize: 14, 
            color: "#555",
            fontWeight: 500
          }}>
            Catégorie
          </label>
          <select 
            value={filterCategory} 
            onChange={e => setFilterCategory(e.target.value)} 
            style={inputStyle}
          >
            <option value="">Toutes les catégories</option>
            {categories.map(c => (
              <option key={c.id} value={c.name}>{c.name}</option>
            ))}
          </select>
        </div>

      
        {/* Reset Button - New row */}
        {(filterBrand || filterCategory || searchQuery) && (
          <div style={{
            gridColumn: "1 / -1",
            display: "flex",
            justifyContent: "flex-end"
          }}>
            <button
              onClick={handleResetFilters}
              style={{
                ...btnStyle,
                background: "#6c757d",
                fontSize: 13,
                padding: "6px 12px"
              }}
            >
              Réinitialiser les filtres
            </button>
          </div>
        )}
      </div>

      {/* Results Counter */}
      <div style={{ 
        marginBottom: 16, 
        padding: "8px 12px", 
        background: "#f8f9fa", 
        borderRadius: 6,
        fontSize: 14,
        color: "#495057",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}>
        <div>
          {searchQuery ? (
            <span>
              {displayedProducts.length} produit{displayedProducts.length !== 1 ? 's' : ''} trouvé{displayedProducts.length !== 1 ? 's' : ''} pour "<strong>{searchQuery}</strong>"
            </span>
          ) : (
            <span>
              {displayedProducts.length} produit{displayedProducts.length !== 1 ? 's' : ''} affiché{displayedProducts.length !== 1 ? 's' : ''} 
              {allProducts.length > displayedProducts.length && ` sur ${allProducts.length}`}
            </span>
          )}
        </div>
        
        {hasMore && !searchQuery && (
          <button
            onClick={loadMoreProducts}
            style={{
              ...btnStyle,
              background: "#28a745",
              fontSize: 13,
              padding: "4px 12px"
            }}
          >
            Charger plus
          </button>
        )}
      </div>

      {/* Products Table */}
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={th}>Image</th>
            <th style={th}>Nom</th>
            <th style={th}>Marque</th>
            <th style={th}>Catégorie</th>
            <th style={th}>Prix</th>
            <th style={th}>Prix Achat</th>
            <th style={th}>Quantité</th>
            <th style={th}>Ventes</th>
            <th style={th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {displayedProducts.length > 0 ? (
            displayedProducts.map(p => (
              <tr key={p.id}>
                <td style={td}>
                  <img 
                    src={p.imageUrl || "https://via.placeholder.com/40"} 
                    alt={p.name} 
                    style={imageStyle} 
                  />
                </td>
                <td style={td}>{p.name}</td>
                <td style={td}>{p.brandName || "-"}</td>
                <td style={td}>{p.categoryName || "-"}</td>
                <td style={td}>{p.price} DA</td>
                <td style={td}>{p.purchasePrice} DA</td>
                <td style={td}>{p.quantity}</td>
               <td 
               style={{
                  ...td,
                 color: (salesMap[p.id] || 0) === 0 ? "red" : "#16a34a",
                 fontWeight: "bold"
                    }}
                            >
                  {salesMap[p.id] || 0}
                </td>
                <td style={td}>
                  <button 
                    onClick={() => navigate(`/products/edit/${p.id}`)} 
                    style={{...btnStyle, marginRight: 6}}
                  >
                    Modifier
                  </button>
                  <button 
                    onClick={() => handleDelete(p.id)} 
                    style={{ ...btnStyle, background: "#dc3545" }}
                  >
                    Supprimer
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={8} style={{ textAlign: "center", padding: 40 }}>
                {loading ? (
                  <div style={shimmerRowStyle}></div>
                ) : (
                  <div style={{ color: "#666" }}>
                    {searchQuery || filterBrand || filterCategory ? (
                      <>
                        <p style={{ fontSize: 16, marginBottom: 8 }}>
                          Aucun produit trouvé
                        </p>
                        <button
                          onClick={handleResetFilters}
                          style={{
                            ...btnStyle,
                            background: "#6c757d",
                            fontSize: 14
                          }}
                        >
                          Voir tous les produits
                        </button>
                      </>
                    ) : (
                      "Aucun produit disponible"
                    )}
                  </div>
                )}
              </td>
            </tr>
          )}

          {loading && displayedProducts.length > 0 && Array(3).fill(0).map((_, i) => (
            <tr key={`shimmer-${i}`}>
              <td colSpan={8}><div style={shimmerRowStyle}></div></td>
            </tr>
          ))}
        </tbody>
      </table>

      {hasMore && !searchQuery && displayedProducts.length > 0 && (
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <button
            onClick={loadMoreProducts}
            style={{
              ...btnStyle,
              background: "transparent",
              color: "#007bff",
              border: "1px solid #007bff"
            }}
            disabled={loading}
          >
            {loading ? "Chargement..." : "Charger plus de produits"}
          </button>
        </div>
      )}

      <Outlet />
    </div>
  );
}